/**
 * The dashboard's "Action required" section (UI-28), as pure decisions.
 *
 * Data: GET /api/system/action-required
 *   { allClear, message, lastCheckAt, checkFresh, counts: { incidents, bookings },
 *     incidents: [ incident rows, critical first ],
 *     bookings:  [ { bookingId, bookingReference, customer, amount, title, at, productTypes } ] }
 *
 * The rule the section is built around: "Everything is running" is a claim, and it may only be
 * made when it is known to be true right now. Not when the request failed, not when the watch
 * has never run, not when its last check is old, not when anything is still listed.
 */
import { FRESH_MINUTES } from '../../../screens/System&Settings/Monitoring/monitoringView';
import { isActiveIncident, sortBySeverity } from '../../../screens/System&Settings/Monitoring/incidentsView';
import { formatDMYTime } from '../../../utils/dateFormat';

export { FRESH_MINUTES };

/** Where an incident row leads: the Monitoring page the alert emails also link to. */
export const MONITORING_PATH = '/monitoring';

/**
 * The booking detail page, by numeric id (the route the bell and Latest Activities use).
 * A booking reference in that URL opens a demo booking, so without a usable id there is no link.
 */
export const bookingHref = (booking) => {
  const id = booking?.bookingId;
  if (id === null || id === undefined || id === '') return null;
  return /^\d+$/.test(String(id)) ? `/bookings/${id}` : null;
};

/**
 * Everything to act on, in the order it must be read: every paid online booking that is not
 * confirmed first (a customer has been charged), then every unresolved incident, critical first.
 */
export const actionItems = (data) => {
  const bookings = (Array.isArray(data?.bookings) ? data.bookings : [])
    .filter(Boolean)
    .map((b, i) => ({
      kind: 'booking',
      key: `booking:${b.bookingId ?? b.bookingReference ?? i}`,
      booking: b,
      href: bookingHref(b),
    }));
  const incidents = sortBySeverity((Array.isArray(data?.incidents) ? data.incidents : []).filter(isActiveIncident))
    .map((incident, i) => ({
      kind: 'incident',
      key: `incident:${incident.id ?? i}`,
      incident,
      href: MONITORING_PATH,
    }));
  return [...bookings, ...incidents];
};

/** Minutes since the watch last checked, or null when it never did (or the time is unreadable). */
export const checkAgeMinutes = (lastCheckAt, now = Date.now()) => {
  if (!lastCheckAt) return null;
  const at = new Date(lastCheckAt).getTime();
  if (!Number.isFinite(at)) return null;
  return Math.max(0, (now - at) / 60000);
};

/**
 * May the green "Everything is running" line be shown? Only when ALL of these hold:
 * the last request succeeded, the backend says all clear AND fresh, the last check is at most
 * FRESH_MINUTES old by this clock too, and nothing at all is listed.
 */
export const mayShowAllClear = (data, { now = Date.now(), failed = false } = {}) => {
  if (failed || !data || typeof data !== 'object') return false;
  if (data.allClear !== true || data.checkFresh !== true) return false;
  const age = checkAgeMinutes(data.lastCheckAt, now);
  if (age === null || age > FRESH_MINUTES) return false;
  if (actionItems(data).length) return false;
  if ((Number(data.counts?.incidents) || 0) > 0 || (Number(data.counts?.bookings) || 0) > 0) return false;
  return true;
};

/**
 * Why nothing is listed and yet "Everything is running" may not be said. Each case still points
 * at the Monitoring page, which shows the watch's own state.
 */
export const unverifiedReason = (data, now = Date.now()) => {
  const age = checkAgeMinutes(data?.lastCheckAt, now);
  if (age === null) return 'Nothing is listed, but the incident watch has not run yet, so this cannot be confirmed.';
  if (age > FRESH_MINUTES || data?.checkFresh !== true) {
    return `Nothing is listed, but the last check was ${formatDMYTime(data.lastCheckAt)}, more than ${FRESH_MINUTES} minutes ago. The watch may have stopped.`;
  }
  return 'Nothing is listed, but the last check did not come back clean. See Monitoring.';
};

/**
 * What the section shows:
 *   loading    first load, nothing known yet
 *   error      the request failed and there is no list to show
 *   items      something to act on (a later failed refresh is passed along as `error`)
 *   allClear   the one green line
 *   unverified nothing listed, but the claim cannot be made (see unverifiedReason)
 */
export const actionRequiredView = ({ data, error = null, now = Date.now() } = {}) => {
  if (!data) {
    if (error) return { mode: 'error', items: [], error };
    return { mode: 'loading', items: [], error: null };
  }
  const items = actionItems(data);
  if (items.length) return { mode: 'items', items, error: error || null };
  if (error) return { mode: 'error', items: [], error };
  if (mayShowAllClear(data, { now, failed: false })) return { mode: 'allClear', items: [], error: null, lastCheckAt: data.lastCheckAt };
  return { mode: 'unverified', items: [], error: null, reason: unverifiedReason(data, now) };
};

/**
 * Toast a failed load? Always when someone pressed refresh; for the background poll only on the
 * first failure of an outage, so a stopped API does not toast once a minute.
 */
export const shouldToastFailure = ({ manual = false, wasFailing = false } = {}) => Boolean(manual || !wasFailing);
